import React, { useEffect, useMemo, useState } from 'react';
import BubbleCommon, {
  filteredInfluencerTipsText,
} from '@op/components/BubbleCommon';
import CardCommon from '@op/components/CardCommon';
import { useIntl } from 'umi';
import { Bubble, Progress } from 'tea-component';
import { getPercentageValue, getPercentageInfo, arrayToObj } from '@op/utils';
import { reviewsFormat, specialSecimalFormatPercentage } from '@op/utils/value';
import {
  filterInfluencerTagsList,
  // genreCreatorListTagsList,
} from '../../constants';
import { getInfluencerGenreTagsStats } from '@op/services';
import './index.scss';
import { formatTimeToShow } from '@op/utils/time';
import { NO_DATE } from '@op/constants';

export const metricsList = [
  { title: 'Filtered Total', key: 'total', format: reviewsFormat },
  {
    title: 'Partnered',
    key: 'total_partnered',
    format: (value: number) => reviewsFormat(value),
  },
  {
    title: 'Avg Brand Affinity',
    key: 'avg_brand_affinity',
    format: (value: number) =>
      specialSecimalFormatPercentage(value, '-', 0, 0.01, '< 1%'),
  },
];

export default function FilteredInfluencer({ filter, genreCreatorListTagsList }: any) {
  const { formatMessage } = useIntl();
  const [metrics, setMetrics] = useState<any>({
    total: '0',
    total_partnered: '0',
    avg_brand_affinity: '0',
  });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!filter.edition_unified_id) return;
    setLoading(true);
    setList([]);
    getInfluencerGenreTagsStats(filter)
      .then((data: any) => {
        const { list, total, total_partnered, avg_brand_affinity } = data || {};
        setMetrics({
          total: total || 0,
          total_partnered: total_partnered || 0,
          avg_brand_affinity: avg_brand_affinity || 0,
        });
        setList(list || []);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filter]);

  return (
    <CardCommon
      loading={loading}
      loadingHeight={418}
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {formatMessage({ id: 'Filtered Influencer' })}
          <BubbleCommon
            placement="top"
            tipsMap={filteredInfluencerTipsText}
          ></BubbleCommon>
        </div>
      }
      className="filtered-influencer-wrapper"
    >
      <div className="metrics-box">
        {metricsList.map(({ title, key, format }) => {
          return (
            <div key={title} className="metrics-item">
              <label className="title">{formatMessage({ id: title })}</label>
              <label className="value">{format(metrics?.[key] || 0)}</label>
            </div>
          );
        })}
      </div>
      <SegmentationBox filter={filter} data={list} genreCreatorListTagsList={genreCreatorListTagsList}></SegmentationBox>
    </CardCommon>
  );
}

function SegmentationBox({
  data,
  filter,
  genreCreatorListTagsList
}: {
  data: Array<{ key: string; partnered: number; total: number }>;
  filter: any;
  genreCreatorListTagsList: any;
}) {
  const { formatMessage } = useIntl();

  const [segmentationList, partneredTotal, allTotal] = useMemo(() => {
    const valueMap = arrayToObj(data, 'tag');

    let partneredTotal = 0,
      allTotal = data.reduce((aTotal, { total }) => total + aTotal, 0);

    const list = genreCreatorListTagsList
      .filter((item) => filterInfluencerTagsList.includes(item.key))
      .map(({ text, bgColor, key }) => {
        const { total, partnered } = valueMap[key] || {
          partnered: 0,
          total: 0,
        };
        partneredTotal += partnered || 0;

        return {
          text,
          percentage: getPercentageInfo(total, allTotal),
          bgColor,
          value: partnered,
          progress: getPercentageValue(partnered, total) * 100,
          total,
        };
      });
    return [list, partneredTotal, allTotal];
  }, [data]);

  const renderSegmentation = (list: Array<any>) => {
    if (list.length === 0) return null;
    return (
      <div className="chart-common-formatter ">
        <div className="chart-common-formatter-header">
          <div className="chart-common-formatter-header-left">
            {formatTimeToShow(filter)}
          </div>
          <div className="chart-common-formatter-header-right"></div>
        </div>
        <table>
          <tbody>
            {list.map(({ text, bgColor, percentage, total }) => {
              return (
                <tr>
                  <td style={{ textAlign: 'left', fontWeight: 'normal' }}>
                    <span className="chart-common-formatter-marker">
                      <span
                        style={{
                          display: 'inline-block',
                          marginRight: 4,
                          borderRadius: 10,
                          width: 10,
                          height: 10,
                          backgroundColor: bgColor,
                        }}
                      ></span>
                    </span>
                    &nbsp;{text}
                  </td>
                  <td
                    className="bold"
                    style={{ textAlign: 'left', fontWeight: 'normal' }}
                  >
                    {reviewsFormat(total)}({percentage})
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="segmentation-box-wrapper">
      <div className="segmentation-all-info">
        <div className="top-info">
          <label>{formatMessage({ id: 'Segmentation Distribution' })}</label>
          {/* <label className="value">
            {reviewsFormat(partneredTotal)}/{reviewsFormat(allTotal)}
          </label> */}
        </div>
        <Bubble
          content={renderSegmentation(segmentationList)}
          dark
          overlayClassName="tea-overlay--nostyle"
          closeOnScroll
        >
          <div className="bar-progress-box">
            {segmentationList.map(({ bgColor, percentage }) => {
              return (
                <span
                  key={bgColor}
                  className="progress-item"
                  style={{ width: percentage, backgroundColor: bgColor }}
                ></span>
              );
            })}
          </div>
        </Bubble>
      </div>

      <div className="segmentation-box">
        <div className="segmentation-title">
          <label>{formatMessage({ id: 'Distribution' })}</label>
          <label>{formatMessage({ id: 'Partnered/Total' })}</label>
        </div>
        <ul className="segmentation-slice-info">
          {segmentationList.map(({ text, value, bgColor, progress, total }) => {
            return (
              <li key={text} className="segmentation-slice-item">
                <div className="top-info">
                  <label>{text || formatMessage({ id: NO_DATE.EMPTY })}</label>
                  <label className="value">
                    {value}/{total}
                  </label>
                </div>
                <Progress
                  className="bar-progress"
                  percent={progress}
                  strokeColor={bgColor}
                  strokeWidth={6}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
